import AddCircle from '@mui/icons-material/AddCircleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CardActionArea,
  CardContent,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ResponsiveDialog } from '../../../../../../consts/Theme'
import { BoundaryThumbnail } from '../../../../../../features/Inventories/components/BoundaryThumbnail'
import { useDeleteBoundary } from '../../../../../../features/Inventories/queries/useDeleteBoundary'
import { useInventory } from '../../../../../../features/Inventories/queries/useInventory'
import { useSetInventoryStatus } from '../../../../../../features/Inventories/queries/useSetInventoryStatus'

export const BoundariesList = () => {
  const { t } = useTranslation('impact/MapLayerOverlay')
  const { t: cropsT } = useTranslation('@cibo/ui/cropNames')
  const [, setParams] = useSearchParams({ step: 'initial' })
  const { inventoryId } = useParams()
  const navigate = useNavigate()
  const deleteBoundary = useDeleteBoundary(inventoryId)
  const setInventoryStatus = useSetInventoryStatus(inventoryId)
  const [lockDialog, setLockDialog] = useState<{ open: boolean }>({ open: false })
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    boundaryId?: string
    boundaryName?: string
  }>({
    open: false,
  })
  const theme = useTheme()
  const { data: inventory } = useInventory(inventoryId)

  const displayingList = !!inventory?.item.boundaries.length

  return (
    <>
      <CardContent sx={{ padding: 0 }}>
        <Stack sx={{ padding: 2 }}>
          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            {t('inventorySelectInitial')}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {t('inventorySelectInitialDescription')}
          </Typography>
        </Stack>
        <List
          sx={{
            width: '100%',
            maxWidth: 360,
            bgcolor: 'background.paper',
            paddingBlock: displayingList ? 1 : 0,
          }}
        >
          {inventory?.item.boundaries.map((boundary, index) => (
            <Fragment key={boundary.boundaryId}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  pointerEvents: 'all',
                  '.hoverShow': { display: 'none' },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.04),
                    '.hoverShow': {
                      display: 'block',
                    },
                  },
                }}
              >
                <ListItemAvatar>
                  <BoundaryThumbnail
                    boundary={boundary}
                    height={36}
                    width={36}
                    style={{ borderRadius: 0.5 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={boundary.name}
                  secondary={boundary.crops.map(c => cropsT(c.crop)).join(', ')}
                  sx={{ flex: 3, wordWrap: 'break-word' }}
                />
                <Stack sx={{ flex: 1 }} direction="row">
                  <IconButton
                    style={{ pointerEvents: 'all' }}
                    aria-label="edit"
                    onClick={() => setParams({ step: 'details', id: boundary.boundaryId })}
                    size="small"
                    className="hoverShow"
                  >
                    <EditIcon sx={{ height: '20px', width: '20px' }} />
                  </IconButton>
                  <IconButton
                    className="hoverShow"
                    style={{ pointerEvents: 'all' }}
                    edge="end"
                    aria-label="delete"
                    size="small"
                    onClick={() => {
                      setDeleteDialog({
                        open: true,
                        boundaryId: boundary.boundaryId,
                        boundaryName: boundary.name,
                      })
                    }}
                  >
                    <DeleteIcon color="error" sx={{ height: '20px', width: '20px' }} />
                  </IconButton>
                </Stack>
              </ListItem>
            </Fragment>
          ))}
          {displayingList && <Divider />}
          <ListItem
            disablePadding
            sx={{
              paddingTop: displayingList ? 2 : 0,
              paddingBottom: displayingList ? 1 : 0,
              paddingInline: 2,
            }}
          >
            <Button
              onClick={() => setParams({ step: 'creating' })}
              color="secondary"
              style={{ pointerEvents: 'all' }}
              startIcon={<AddCircle />}
              data-testid="addBoundaryBtn"
            >
              {t('addBoundaryArea')}
            </Button>
          </ListItem>
        </List>
      </CardContent>
      {displayingList && (
        <>
          <Divider />
          <CardActionArea
            sx={{ padding: 2, display: 'flex' }}
            style={{ pointerEvents: 'none' }}
            component="div"
          >
            <Button
              style={{ pointerEvents: 'all', width: '100%' }}
              onClick={() => setLockDialog({ open: true })}
              variant="contained"
            >
              {t('setInventoryBoundary')}
            </Button>
          </CardActionArea>
        </>
      )}
      <ResponsiveDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, boundaryId: undefined })}
      >
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body1">
              {t('deleteDialogTitle', { name: deleteDialog.boundaryName })}
            </Typography>
            <Typography variant="subtitle2">{t('deleteDialogMessage')}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })}>{t('cancel')}</Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={() => {
              if (!inventoryId || !deleteDialog.boundaryId) return
              deleteBoundary
                .mutateAsync({ inventoryId, boundaryId: deleteDialog.boundaryId })
                .then(a => setDeleteDialog({ open: false }))
            }}
            loading={deleteBoundary.isLoading}
          >
            {t('deleteBoundaryArea')}
          </LoadingButton>
        </DialogActions>
      </ResponsiveDialog>
      <ResponsiveDialog open={lockDialog.open} onClose={() => setLockDialog({ open: false })}>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body1">{t('lockDialogTitle')}</Typography>
            <Typography variant="subtitle2">{t('lockDialogMessage')}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockDialog({ open: false })}>{t('keepEditing')}</Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={() => {
              setInventoryStatus.mutateAsync({ status: 'locked' }).then(a => {
                setLockDialog({ open: false })
                navigate('./')
              })
            }}
            loading={deleteBoundary.isLoading}
          >
            {t('setBoundary')}
          </LoadingButton>
        </DialogActions>
      </ResponsiveDialog>
    </>
  )
}
